import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Loader2, X } from 'lucide-react';
import { apiFetch } from '../../shared/apiClient';
import type { Product } from '../../contexts/AuthContext';
import { ConflictResolutionModal } from './ConflictResolutionModal';
import { ReauthModal } from './ReauthModal';
import './AddProductModal.css';

type SubmitError = { kind: 'product_limit'; max?: number } | { kind: 'plain'; message: string };

type ConflictPayload = {
  existing: { id: string; name: string };
  jiraKey: string;
  name: string;
  isDefault: boolean;
};

export interface AddProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  organizationId: string;
  /** Called after successful create with the new product row */
  onCreated: (product: Product) => void;
  /** Shown in the re-authentication step (must match the signed-in account). */
  userEmail?: string | null;
  /** When the user chooses to keep the existing product that already owns the Jira key. */
  onUseExistingProduct?: (existing: { id: string; name: string }) => void | Promise<void>;
}

export function AddProductModal({
  isOpen,
  onClose,
  organizationId,
  onCreated,
  userEmail,
  onUseExistingProduct,
}: AddProductModalProps) {
  const [name, setName] = useState('');
  const [jiraKey, setJiraKey] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [submitError, setSubmitError] = useState<SubmitError | null>(null);
  const [loading, setLoading] = useState(false);
  const [conflict, setConflict] = useState<ConflictPayload | null>(null);
  const [reauthOpen, setReauthOpen] = useState(false);
  const [replaceLoading, setReplaceLoading] = useState(false);
  const [reauthError, setReauthError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setName('');
      setJiraKey('');
      setIsDefault(false);
      setSubmitError(null);
      setLoading(false);
      setConflict(null);
      setReauthOpen(false);
      setReplaceLoading(false);
      setReauthError(null);
    }
  }, [isOpen]);

  const handleReplaceConfirm = useCallback(
    async (password: string) => {
      if (!conflict) return;
      setReauthError(null);
      setReplaceLoading(true);
      try {
        const res = await apiFetch('/api/products/replace-with-key', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Organization-Id': organizationId,
          },
          body: JSON.stringify({
            name: conflict.name,
            jira_project_key: conflict.jiraKey,
            is_default: conflict.isDefault,
            password,
            expected_existing_product_id: conflict.existing.id,
          }),
        });
        const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
        if (res.status === 401 && data.error === 'invalid_password') {
          setReauthError('Current password is incorrect.');
          return;
        }
        if (res.status === 503 && data.error === 'auth_not_configured') {
          setReauthError(
            typeof data.message === 'string'
              ? data.message
              : 'Password verification is not configured on the server.'
          );
          return;
        }
        if (res.status === 429 || data.error === 'too_many_attempts') {
          setReauthError('Too many attempts. Please wait a few minutes and try again.');
          return;
        }
        if (res.status === 403 && data.error === 'product_limit_reached') {
          setReauthError(
            typeof data.max === 'number'
              ? `Your plan allows up to ${data.max} products.`
              : 'Product limit reached for your plan.'
          );
          return;
        }
        if (res.status === 409 && data.error === 'conflict_changed') {
          setReauthError(
            'This conflict changed (another admin may have updated products). Close and try again.'
          );
          return;
        }
        if (!res.ok) {
          setReauthError(
            typeof data.error === 'string' ? data.error : 'Failed to replace product.'
          );
          return;
        }
        const { replaced: _r, deleted_product_id: _d, ...product } = data;
        void _r;
        void _d;
        onCreated(product as unknown as Product);
        setConflict(null);
        setReauthOpen(false);
        onClose();
      } catch {
        setReauthError('Network error');
      } finally {
        setReplaceLoading(false);
      }
    },
    [conflict, organizationId, onCreated, onClose]
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      const trimmed = name.trim();
      if (!trimmed) {
        setSubmitError({ kind: 'plain', message: 'Product name is required' });
        return;
      }
      setSubmitError(null);
      setLoading(true);
      try {
        const jiraNorm = jiraKey.trim() ? jiraKey.trim().toUpperCase() : null;
        const res = await apiFetch('/api/products', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Organization-Id': organizationId,
          },
          body: JSON.stringify({
            name: trimmed,
            jira_project_key: jiraNorm,
            is_default: isDefault,
          }),
        });
        const data = (await res.json().catch(() => ({}))) as {
          error?: string;
          message?: string;
          max?: number;
          existing_product?: { id: string; name: string } | null;
        };
        if (res.status === 409) {
          const _errStr = typeof data.error === 'string' ? data.error : '';
          const msgStr = typeof data.message === 'string' ? data.message : '';
          const isDefaultOnlyConflict =
            msgStr.includes('Only one default product') ||
            msgStr.includes('default product is allowed');

          if (jiraNorm && !isDefaultOnlyConflict) {
            let existingId = data.existing_product?.id;
            let existingName = data.existing_product?.name ?? '';

            if (!existingId) {
              const listRes = await apiFetch('/api/products', {
                method: 'GET',
                headers: { 'X-Organization-Id': organizationId },
              });
              if (listRes.ok) {
                const listBody = (await listRes.json().catch(() => ({}))) as {
                  products?: Array<{ id: string; name: string; jira_project_key?: string | null }>;
                };
                const match = listBody.products?.find((p) => {
                  const k = p.jira_project_key;
                  if (k == null || !String(k).trim()) return false;
                  return String(k).trim().toUpperCase() === jiraNorm;
                });
                if (match) {
                  existingId = match.id;
                  existingName = match.name;
                }
              }
            }

            if (existingId) {
              setConflict({
                existing: { id: existingId, name: existingName || 'Product' },
                jiraKey: jiraNorm,
                name: trimmed,
                isDefault,
              });
              return;
            }
          }

          setSubmitError({
            kind: 'plain',
            message:
              msgStr || (typeof data.error === 'string' ? data.error : 'Failed to create product'),
          });
          return;
        }
        if (res.status === 403 && data.error === 'product_limit_reached') {
          setSubmitError({
            kind: 'product_limit',
            max: typeof data.max === 'number' ? data.max : undefined,
          });
          return;
        }
        if (!res.ok) {
          setSubmitError({
            kind: 'plain',
            message: typeof data.error === 'string' ? data.error : 'Failed to create product',
          });
          return;
        }
        onCreated(data as Product);
        onClose();
      } catch {
        setSubmitError({ kind: 'plain', message: 'Network error' });
      } finally {
        setLoading(false);
      }
    },
    [name, jiraKey, isDefault, organizationId, onCreated, onClose]
  );

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape' && !conflict && !reauthOpen) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose, conflict, reauthOpen]);

  if (!isOpen) return null;

  if (typeof document === 'undefined' || !document.body) return null;

  return createPortal(
    <>
      <div
        className="add-product-modal-overlay"
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-product-modal-title"
        onClick={(e) => {
          if (e.target === e.currentTarget && !conflict && !reauthOpen) onClose();
        }}
      >
        <div className="add-product-modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="add-product-modal-header">
            <h2 id="add-product-modal-title">Add product</h2>
            <button
              type="button"
              className="add-product-modal-close"
              onClick={onClose}
              aria-label="Close"
            >
              <X size={20} aria-hidden />
            </button>
          </div>
          <form onSubmit={handleSubmit} noValidate>
            <div className="add-product-modal-body">
              {submitError ? (
                <div className="add-product-modal-error" role="alert">
                  {submitError.kind === 'product_limit' ? (
                    <>
                      <p className="add-product-modal-error-text">
                        {submitError.max != null
                          ? `Your current plan allows up to ${submitError.max} products. To add another, remove an existing product or upgrade your plan.`
                          : 'Your organization has reached the product limit for its current plan. Remove an existing product or upgrade your plan to add more.'}
                      </p>
                      <a
                        href="/settings?tab=billing"
                        className="add-product-modal-error-link"
                        onClick={onClose}
                      >
                        Open billing and plans
                      </a>
                    </>
                  ) : (
                    <p className="add-product-modal-error-text">{submitError.message}</p>
                  )}
                </div>
              ) : null}
              <div className="form-group">
                <label htmlFor="add-product-name" className="form-label">
                  Product name <span aria-hidden="true">*</span>
                </label>
                <input
                  id="add-product-name"
                  type="text"
                  className="form-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoComplete="off"
                  maxLength={200}
                  required
                  disabled={loading}
                />
              </div>
              <div className="form-group">
                <label htmlFor="add-product-jira" className="form-label">
                  Jira project key
                </label>
                <input
                  id="add-product-jira"
                  type="text"
                  className="form-input"
                  placeholder="e.g. PROJ"
                  value={jiraKey}
                  onChange={(e) => setJiraKey(e.target.value.toUpperCase())}
                  autoComplete="off"
                  maxLength={32}
                  disabled={loading}
                />
                <span className="form-hint">
                  Optional. Must be unique within this organization.
                </span>
              </div>
              <div className="form-group">
                <div className="add-product-modal-checkbox-row">
                  <input
                    id="add-product-default"
                    type="checkbox"
                    checked={isDefault}
                    onChange={(e) => setIsDefault(e.target.checked)}
                    disabled={loading}
                  />
                  <label htmlFor="add-product-default">
                    Set as default product
                    <span className="form-hint" style={{ display: 'block', marginTop: 4 }}>
                      Only one product can be the default. The previous default is cleared
                      automatically.
                    </span>
                  </label>
                </div>
              </div>
            </div>
            <div className="add-product-modal-footer">
              <button
                type="button"
                className="add-product-modal-btn-secondary"
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </button>
              <button type="submit" className="add-product-modal-btn-primary" disabled={loading}>
                {loading ? <Loader2 className="add-product-modal-spin" size={18} /> : null}
                {loading ? 'Creating…' : 'Create product'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {conflict ? (
        <ConflictResolutionModal
          isOpen={!reauthOpen}
          jiraKey={conflict.jiraKey}
          existingProductName={conflict.existing.name}
          onCancel={() => setConflict(null)}
          onUseExisting={async () => {
            await onUseExistingProduct?.({
              id: conflict.existing.id,
              name: conflict.existing.name,
            });
            setConflict(null);
            onClose();
          }}
          onDeleteAndRecreate={() => {
            setReauthOpen(true);
          }}
        />
      ) : null}

      {conflict && reauthOpen ? (
        <ReauthModal
          isOpen
          userEmail={userEmail ?? ''}
          existingProductName={conflict.existing.name}
          newProductName={conflict.name}
          jiraKey={conflict.jiraKey}
          loading={replaceLoading}
          error={reauthError}
          onCancel={() => {
            setReauthOpen(false);
            setReauthError(null);
          }}
          onConfirm={handleReplaceConfirm}
        />
      ) : null}
    </>,
    document.body
  );
}
